import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronRight, Check, ChevronDown, ArrowLeft } from 'lucide-react';
import { pricingPlans, BILLING_PERIODS, getPlanById } from '@/config/pricingPlans';
import { trackPricingToggle, trackPricingPrimaryCta } from '@/utils/analytics';

const PlanDetailPage = ({ planId }) => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState(BILLING_PERIODS.MONTHLY);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  const plan = getPlanById(planId);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [planId]);
  
  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Plan not found</h1>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  const IconComponent = plan.icon;
  const detail = plan.detailPage;

  const handleToggle = (period) => {
    setBillingPeriod(period);
    trackPricingToggle(period);
  };

  const handlePrimaryCta = () => {
    trackPricingPrimaryCta(plan.id, billingPeriod);
    let route = plan.primaryCta.route;
    if (plan.pricing.type === 'subscription' && route.includes('/checkout')) {
      route += `&billing=${billingPeriod}`;
    }
    navigate(route);
  };

  const getDisplayPrice = () => {
    if (plan.pricing.type === 'one-time') {
      return {
        amount: `$${plan.pricing.amount}`,
        label: plan.pricing.label,
        savings: null
      };
    }

    if (plan.pricing.type === 'per-case') {
      return {
        amount: `$${plan.pricing.amount}`,
        label: '/case',
        savings: null
      };
    }

    if (plan.pricing.type === 'annual-only') {
      return {
        amount: `$${plan.pricing.amount}`,
        label: '/yr',
        savings: null
      };
    }
    
    if (billingPeriod === BILLING_PERIODS.ANNUAL) {
      return {
        amount: `$${plan.pricing.annualAmount.toLocaleString()}`,
        label: '/yr',
        savings: plan.pricing.annualSavings
      };
    }
    
    return {
      amount: `$${plan.pricing.monthlyAmount}`,
      label: '/mo',
      savings: null
    };
  };

  const priceDisplay = getDisplayPrice();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="py-16 px-6" style={{ backgroundColor: '#f8fafc' }}>
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/#pricing" 
            className="inline-flex items-center gap-2 text-sm font-medium mb-8 hover:underline"
            style={{ color: '#70767C' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Pricing
          </Link>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${plan.color}15` }}
                >
                  <IconComponent className="w-8 h-8" style={{ color: plan.color }} />
                </div>
                {plan.badge && (
                  <span 
                    className="text-sm font-semibold px-3 py-1 rounded-full text-white"
                    style={{ backgroundColor: plan.color }}
                  >
                    {plan.badge}
                  </span>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: '#092036' }}>
                {plan.name}
              </h1>
              <p className="text-lg" style={{ color: '#70767C' }}>
                {detail.heroSubtitle}
              </p>
            </div>

            {/* Price Card */}
            <Card className="p-6 bg-white shadow-lg min-w-[280px]">
              {/* Billing Toggle for Subscriptions */}
              {plan.pricing.type === 'subscription' && (
                <div className="flex items-center justify-center mb-4">
                  <div className="inline-flex items-center p-1 bg-gray-100 rounded-full">
                    <button
                      onClick={() => handleToggle(BILLING_PERIODS.MONTHLY)}
                      className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                        billingPeriod === BILLING_PERIODS.MONTHLY
                          ? 'bg-white shadow-sm text-gray-900'
                          : 'text-gray-600'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => handleToggle(BILLING_PERIODS.ANNUAL)}
                      className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                        billingPeriod === BILLING_PERIODS.ANNUAL
                          ? 'bg-white shadow-sm text-gray-900'
                          : 'text-gray-600'
                      }`}
                    >
                      Annual
                    </button>
                  </div>
                </div>
              )}

              <div className="text-center mb-4">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold" style={{ color: '#092036' }}>
                    {priceDisplay.amount}
                  </span>
                  <span className="text-lg" style={{ color: '#70767C' }}>
                    {priceDisplay.label}
                  </span>
                </div>
                {priceDisplay.savings && (
                  <p className="text-sm font-medium mt-1" style={{ color: '#16a34a' }}>
                    {priceDisplay.savings}
                  </p>
                )}
              </div>

              <Button
                onClick={handlePrimaryCta}
                className="w-full py-5 text-base font-semibold rounded-xl transition-all text-white"
                style={{ backgroundColor: plan.color }}
              >
                {plan.primaryCta.text}
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-8" style={{ color: '#092036' }}>
            What You Get
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {detail.whatYouGet.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: plan.color }} />
                <span style={{ color: '#4B5563' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 px-6" style={{ backgroundColor: '#f8fafc' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-8" style={{ color: '#092036' }}>
            Who This Is For
          </h2>
          <div className="space-y-4">
            {detail.whoIsThisFor.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${plan.color}15` }}
                >
                  <span className="font-semibold" style={{ color: plan.color }}>{idx + 1}</span>
                </div>
                <p className="text-lg pt-1" style={{ color: '#4B5563' }}>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-8" style={{ color: '#092036' }}>
            How It Works
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {detail.howItWorks.map((step, idx) => (
              <div key={idx} className="text-center">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: plan.color }}
                >
                  <span className="text-xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="font-semibold mb-2" style={{ color: '#092036' }}>
                  {step.title}
                </h3>
                <p className="text-sm" style={{ color: '#70767C' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scope & Disclaimer (Curbside Consult only) */}
      {detail.scopeDisclaimer && (
        <section className="py-12 px-6" style={{ backgroundColor: '#FEF3C7' }}>
          <div className="max-w-4xl mx-auto">
            <h3 className="font-semibold mb-4" style={{ color: '#92400E' }}>
              {detail.scopeDisclaimer.title}
            </h3>
            <ul className="space-y-2">
              {detail.scopeDisclaimer.points.map((point, idx) => (
                <li key={idx} className="text-sm" style={{ color: '#92400E' }}>
                  • {point}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="py-16 px-6" style={{ backgroundColor: '#f8fafc' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-8" style={{ color: '#092036' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {detail.faqs.map((faq, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium pr-4" style={{ color: '#092036' }}>
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${
                      expandedFaq === idx ? 'rotate-180' : ''
                    }`}
                    style={{ color: '#70767C' }}
                  />
                </button>
                {expandedFaq === idx && (
                  <div className="px-5 pb-5">
                    <p style={{ color: '#4B5563' }}>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg md:hidden z-50">
        <Button
          onClick={handlePrimaryCta}
          className="w-full py-4 text-base font-semibold rounded-xl text-white"
          style={{ backgroundColor: plan.color }}
        >
          {plan.primaryCta.text}
          <ChevronRight className="w-5 h-5 ml-1" />
        </Button>
      </div>

      {/* Bottom padding for sticky CTA on mobile */}
      <div className="h-24 md:hidden" />
    </div>
  );
};

export default PlanDetailPage;
